import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Account } from '../types';
import { getOrCreateSessionId } from '../lib/spaces';
import { useChat } from '../lib/chatContext';
import { useActivityFeed } from '../lib/activityFeed';
import { ChatMessage } from './ChatMessage';
import { LoaderIcon, BotIcon, AlertCircleIcon, ExternalLinkIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type Props = {
  account: Account;
  botType: 'internal' | 'external';
  section?: 'overview' | 'people' | 'orders' | 'assets' | 'analytics' | 'spaces';
  onNavigate?: (view: string, metadata?: any) => void;
  onAction?: (action: string, metadata?: any) => void;
};
export function LiveChatPreview({
  account,
  botType,
  section = 'overview',
  onNavigate,
  onAction
}: Props) {
  const bot = useMemo(() => account.bots.find(b => b.type === botType && b.api_endpoint), [account, botType]);
  const [input, setInput] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const sessionId = getOrCreateSessionId(account.id);
  const {
    getSession,
    sendMessage,
    isLoading,
    updateContext
  } = useChat();
  const {
    addActivity
  } = useActivityFeed();
  const session = getSession(account.id);
  const loading = isLoading(account.id);
  // Update context when section or account changes
  useEffect(() => {
    updateContext(account.id, {
      accountId: account.id,
      accountName: account.name,
      section
    });
  }, [account.id, account.name, section, updateContext]);
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [session.messages]);
  if (!bot?.api_endpoint) {
    return <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gradient-to-br from-slate-950/60 via-slate-900/40 to-slate-950/60 rounded-xl border border-slate-800/50" data-id="element-758">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 flex items-center justify-center mb-6 shadow-2xl shadow-black/40" data-id="element-759">
          <AlertCircleIcon className="w-12 h-12 text-slate-500" data-id="element-760" />
        </motion.div>
        <h3 className="text-lg font-semibold text-white mb-2.5 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent" data-id="element-761">
          Chat Not Available
        </h3>
        <p className="text-sm text-slate-400 mb-2 max-w-sm leading-relaxed" data-id="element-762">
          This bot does not have an API endpoint configured.
        </p>
        <p className="text-xs text-slate-500 max-w-md" data-id="element-763">
          Please contact your administrator to set up the chat integration.
        </p>
      </div>;
  }
  const handleSendMessage = async () => {
    const text = input.trim();
    if (!text) return;
    // Guard: check if API endpoint exists
    if (!bot.api_endpoint) {
      console.error('No API endpoint configured for bot:', bot.name);
      return;
    }
    // Log activity
    const preview = text.length > 50 ? text.substring(0, 50) + '...' : text;
    addActivity(account.id, {
      type: 'chat',
      message: `You asked: "${preview}"`
    });
    setInput('');
    await sendMessage(account.id, bot.api_endpoint, sessionId, text);
    // Log response activity
    setTimeout(() => {
      addActivity(account.id, {
        type: 'chat',
        message: `${bot.name} provided a response`
      });
    }, 1000);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleChatAction = (action: string, metadata?: any) => {
    if (action === 'chat:focus') {
      const inputElement = document.querySelector(`input[placeholder*="${bot.name}"]`) as HTMLInputElement;
      inputElement?.focus();
    } else {
      onAction?.(action, metadata);
    }
  };
  return <div className="h-full flex flex-col rounded-xl border border-slate-800/80 bg-gradient-to-br from-slate-950/60 via-slate-900/40 to-slate-950/60 overflow-hidden shadow-2xl shadow-black/30" data-id="element-764">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-950/90 backdrop-blur-sm" data-id="element-765">
        <div className="flex items-center gap-3" data-id="element-766">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-xl shadow-blue-600/30 ring-2 ring-blue-500/20" data-id="element-767">
            <BotIcon className="w-5 h-5 text-white" data-id="element-768" />
          </div>
          <div data-id="element-769">
            <span className="text-sm font-semibold text-white block leading-tight bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-770">
              {bot.name}
            </span>
            <span className="text-xs text-slate-400 font-medium" data-id="element-771">
              AI Assistant
            </span>
          </div>
        </div>
        <button onClick={() => window.open(bot.link, '_blank')} className="px-4 py-2.5 bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 hover:border-slate-600 text-white text-xs font-medium rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 hover:scale-105 active:scale-95" data-id="element-772">
          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-773" />
          Open Full Chat
        </button>
      </div>
      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-slate-950/30 via-slate-900/20 to-slate-950/40 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-774">
        {session.messages.length === 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-775">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600/20 via-blue-500/10 to-purple-600/20 border border-blue-500/30 flex items-center justify-center mb-6 shadow-2xl shadow-blue-600/20" data-id="element-776">
              <SparklesIcon className="w-10 h-10 text-blue-400" data-id="element-777" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2.5 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-778">
              Start a Conversation
            </h3>
            <p className="text-sm text-slate-400 mb-2 max-w-xs leading-relaxed" data-id="element-779">
              Ask {bot.name} anything about {account.name}
            </p>
            <p className="text-xs text-slate-500 max-w-sm leading-relaxed" data-id="element-780">
              Get instant answers about orders, team members, analytics, and
              more
            </p>
          </motion.div>}
        {session.messages.map(msg => <ChatMessage key={msg.id} message={msg} isUser={msg.role === 'user'} onNavigate={onNavigate} onAction={handleChatAction} accountContext={{
        accountId: account.id,
        accountName: account.name
      }} data-id="element-781" />)}
        {loading && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-3 text-slate-400 px-4 py-3.5 bg-slate-900/60 border border-slate-800/60 rounded-xl shadow-lg shadow-black/20" data-id="element-782">
            <LoaderIcon className="w-4 h-4 animate-spin text-blue-400" data-id="element-783" />
            <span className="text-sm font-medium" data-id="element-784">
              {bot.name} is thinking...
            </span>
          </motion.div>}
      </div>
      {/* Input */}
      <div className="p-5 border-t border-slate-800/80 bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-950/90 backdrop-blur-sm" data-id="element-785">
        <div className="flex gap-3" data-id="element-786">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder={`Message ${bot.name}...`} disabled={loading} className="flex-1 bg-slate-900/80 border border-slate-700/60 focus:border-blue-500 hover:border-slate-600 rounded-xl px-4 py-3.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-inner shadow-black/20 hover:shadow-black/30" data-id="element-787" />
          <button onClick={handleSendMessage} disabled={!input.trim() || loading} className="px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-800 disabled:cursor-not-allowed rounded-xl text-white transition-all flex items-center gap-2 shadow-xl shadow-blue-600/30 disabled:shadow-none font-medium hover:scale-105 active:scale-95 disabled:hover:scale-100" data-id="element-788">
            <SendIcon className="w-4 h-4" data-id="element-789" />
            <span className="hidden sm:inline" data-id="element-790">Send</span>
          </button>
        </div>
      </div>
    </div>;
}